export type EmptyResult = NonNullable<unknown>;

export interface ServiceError {
    key?: string;
    rawValue?: unknown;
    message: string | null;
}

export interface ProblemDetails {
    type: string | null;
    title: string | null;
    status: number | null;
    detail: string | null;
    instance: string | null;
    exception?: ServiceErrorExceptionInfo | null;
    errors?: ServiceError[] | null;
    [key: string]: unknown;
}

export interface ServiceErrorExceptionInfo {
    message: string;
    detail: string;
    stackTrace?: string | null;
    exceptionType?: string | null;
    innerException?: ServiceErrorExceptionInfo | null;
}

export type AuthTokens = {
    access_token: string;
    refresh_token: string;
};

export type Authorization = {
    authorize?: boolean;
    roles?: string[];
};

interface AuthBase {
    roles: string[];
}

interface UnauthenticatedState extends AuthBase {
    isAuthenticated: false;
}

export interface BaseAuthenticatedState
    extends AuthBase, Record<string, unknown> {
    isAuthenticated: true;
    id: string;
    unique_name: string;
    email: string;
    given_name: string;
    family_name: string;
    can_change_password: boolean;
    hasCustomerLogin: boolean;
}

export interface CustomerAuthenticatedState extends BaseAuthenticatedState {
    hasCustomerLogin: true;
    customer_id: string;
    person_id: string;
    has_multiple_customers: boolean;
}

export interface NonCustomerAuthenticatedState extends BaseAuthenticatedState {
    hasCustomerLogin: false;
}

export type AuthData =
    | UnauthenticatedState
    | CustomerAuthenticatedState
    | NonCustomerAuthenticatedState;

export type AuthError = {
    error: string;
    error_description: string;
};

export type AuthResult = {
    authTokens?: AuthTokens;
    authError?: AuthError;
    authenticated: boolean;
};

export enum LoginProvider {
    BuiltIn = 0,
    Email = 1,
    Microsoft = 2,
    Google = 3
}

export type SocialAuthResult = AuthResult & {
    provider: LoginProvider;
    token: string;
    lastName?: string;
    firstName?: string;
    email?: string;
};

export interface LoginWithEmailModel {
    email: string;
    redir?: string | null;
    turnstileToken?: string | null;
}

export type ResendEmailConfirmationEmailCommand = {
    email: string;
    turnstileToken?: string | null;
};

export type SendEmailConfirmationEmailCommand = {
    userId: string;
};

export type UpdateUserModel = {
    userId: string;
    emailConfirmed?: boolean | null;
    userRoleNames?: string[] | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    password?: string | null;
    isActive?: boolean | null;
};

export interface PagedResult<TItem> {
    page: number;
    perPage: number;
    count: number;
    total: number;
    isFirst: boolean;
    isLast: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
    totalPages: number;
    items: TItem[];
}

export interface UserInfoModel {
    id: string;
    email: string;
    emailConfirmed: boolean;
    registered: boolean;
    firstName: string;
    lastName: string;
    roles: string[];
    loginProviders: LoginProvider[];
    customers: CustomerInfoModel[];
    isActive: boolean;
}

export interface CustomerInfoModel {
    id: string;
    name: string;
    timeZone: string;
    isActive: boolean;
    createdAt: string;
    peopleCount: number;
    projectCount: number;
    // Billing posture: only populated by the SysAdmin customers list.
    subscriptionMode?: SubscriptionEnforcementMode | null;
    subscriptionReason?: string | null;
    subscriptionOverrideExpiresOn?: string | null;
    /** AI spend in the last 30 days, USD (SysAdmin customers list only). */
    aiSpend30dUsd?: number | null;
}

// A company (internally a "customer") the logged-in user is a member of.
// Surfaced to the user as a "company" in the company switcher.
export interface UserCompanyModel {
    customerId: string;
    name: string;
    isCurrentlySelected: boolean;
}

export interface SysAdminCustomerDetailModel {
    id: string;
    name: string;
    timeZone: string;
    isActive: boolean;
    allowPlatformAiFallback: boolean;
    /** The customer's own AI policy (normalized): 'disabled' | 'ownKeysOnly' | 'platformAllowed'. */
    aiPolicy: string;
    createdAt: string;
    updatedAt: string | null;
    paddleCustomerId: string | null;
    peopleCount: number;
    projectCount: number;
    teamCount: number;
    allocationCount: number;
    authorizedDomainCount: number;
    /** The requesting sysadmin has a person record in this company. */
    currentUserIsMember: boolean;
    /** The requesting sysadmin's session is scoped to this company, so deletion is refused. */
    currentUserIsSignedInHere: boolean;
    authorizedDomains: string[];
    people: SysAdminCustomerPersonModel[];
    subscriptions: SysAdminSubscriptionModel[];
    // Enforcement (Theme 11)
    subscriptionOverrideExpiresOn: string | null;
    subscriptionOverrideNote: string | null;
    billingExempt: boolean;
    billingExemptNote: string | null;
    billingTransitionAnnouncedAt: string | null;
    enforcementActive: boolean;
    enforcementMode: string;
    enforcementReason: string;
    gracedUntil: string | null;
    maxMaintainerSeats: number;
    usedMaintainerSeats: number;
    maxResources: number;
    usedResources: number;
    /** 'none' | 'sandbox' | 'production' */
    billingMode: BillingEnvironment;
    // The price set the paid platform subscription was bought on (null on a trial).
    priceSetName: string | null;
    priceSetStatus: BillingPriceSetStatus | null;
    billingInterval: BillingInterval | null;
}

// Result of a sysadmin hard-deleting a customer. When the acting sysadmin was a
// member of the deleted company their session is re-issued, because their old
// token still names it in the customer_id / has_multiple_customers claims.
export interface HardDeleteCustomerResultModel {
    sessionReissued: boolean;
    authTokens: AuthTokens | null;
}

export interface SysAdminSubscriptionModel {
    id: string;
    paddleSubscriptionId: string | null;
    paddleStatus: string | null;
    paddleProductId: string | null;
    paddlePriceId: string | null;
    quantity: number;
    isActive: boolean;
    startDate: string;
    endDate: string | null;
    paddleNextBillDate: string | null;
    paddleTrialEndsAt: string | null;
    paddleCanceledAt: string | null;
    createdAt: string;
}

export interface SysAdminCustomerPersonModel {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    roles: string[];
}

export interface UpdateCustomerModel {
    customerId: string;
    name?: string | null;
    timeZone?: string | null;
    isActive?: boolean | null;
    allowPlatformAiFallback?: boolean | null;
}

export type ChangePasswordCommand = {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
};

export type EmailConfirmationCommand = {
    userId: string;
    token: string;
    qs: string;
};

export type ForgotPasswordCommand = {
    email: string;
    turnstileToken?: string | null;
};

export interface UserRegistrationEmailLoginCommand {
    firstName: string;
    lastName: string;
    email: string;
    timeZone: string;
    redir: string | null;
    turnstileToken?: string | null;
}

export type UserRegistrationBuiltInCommand = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    timeZone: string;
    redir: string | null;
    turnstileToken?: string | null;
};

export type UserRegistrationSocialCommand = {
    firstName: string;
    lastName: string;
    email: string;
    provider: LoginProvider;
    token: string;
    timeZone: string;
    redir: string | null;
};

interface ITokenInfo {
    provider: LoginProvider;
    token: string;
}

export type SocialAuthLoginCommand = ITokenInfo;

export type TokenModel = {
    token: string;
};

export type ResetPasswordCommand = {
    userId: string;
    newPassword: string;
    confirmPassword: string;
    token: string;
};

export interface CompanyRegistrationCommand {
    customerName: string;
    timeZone: string;
    authorizePrimaryDomain?: boolean;
    administratorContact: ContactPersonCommand;
    additionalContacts: ContactPersonCommand[];
    /** Email the additional contacts an invitation to sign in. */
    inviteAdditionalContacts?: boolean;
}

export interface CustomerDetailsModel {
    id: string;
    name: string;
    timeZone: string;
    isActive: boolean;
    createdAt: string;
}

export interface UpdateCustomerDetailsCommand {
    name: string;
    timeZone: string;
}

export interface AuthorizedDomainDto {
    id: string;
    domainName: string;
    notes: string | null;
    isActive: boolean;
    createdAt: string;
    createdByName: string;
}

export interface AddAuthorizedDomainCommand {
    domainName: string;
    notes?: string;
}

export interface ContactPersonCommand {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle?: string;
    phoneNumber?: string;
    roles: CustomerRole[];
}

export enum CustomerRole {
    Administrator = 0,
    TechnicalContact = 1,
    BillingProcurement = 2,
    SupportContact = 3,
    // Billable "doing stuff" seat — can create projects and manage people,
    // teams, tags and job roles. Administrators imply this capability.
    Maintainer = 4
}

export interface MarkdownDocumentModel {
    id: string | null;
    documentGroup: DocumentGroup;
    path: string;
    directory: string;
    content: string;
    createdAt: string;
    updatedAt: string | null;
    published: boolean;
    isRepoSynced: boolean;
    // Media documents (path ends with .mp4/.mp3/.m4a/.wav)
    hasMedia: boolean;
    transcriptionStatus: TranscriptionStatus;
    // Original raw transcription (pre AI-formatting); only populated on
    // editor/admin reads, so it can be reviewed against the formatted content.
    rawTranscript: string | null;
    // Why the last transcription attempt failed (set when transcriptionStatus is
    // Failed); only populated on editor/admin reads.
    transcriptionError: string | null;
    // Content files (pasted images, attachments) the published content and the draft
    // reference; only populated on editor/admin reads.
    embeddedFiles: string[];
    draftEmbeddedFiles: string[];
    // Discovery synopsis the documentation assistant uses to decide whether to
    // open this document; generated in the background from the published
    // content. Only populated on editor/admin reads.
    synopsis: string | null;
    synopsisGeneratedAt: string | null;
    // Whether the synopsis was generated from the current published content.
    synopsisIsCurrent: boolean;
    hasDraft: boolean;
    // Only populated on editor/admin reads, never on the public endpoint.
    draftContent: string | null;
    draftUpdatedAt: string | null;
    draftAuthor: string | null;
}

export enum TranscriptionStatus {
    None = 0,
    Transcribing = 1,
    Transcribed = 2,
    Failed = 3
}

// A bulk authored-docs push runs as a background job, so the UI follows it by status.
export enum AuthoredDocsPushStatus {
    Queued = 0,
    Running = 1,
    Completed = 2,
    Failed = 3
}

export type DocumentGroup = 'docs' | 'sys';

export interface MarkdownDocumentVersionModel {
    id: string;
    markdownDocumentId: string;
    author: string;
    createdAt: string;
}

export enum DiffLineType {
    Unchanged = 0,
    Added = 1,
    Removed = 2
}

export interface DiffLineModel {
    type: DiffLineType;
    text: string;
}

export interface DiffModel {
    lines: DiffLineModel[];
}

export interface MarkdownDocumentVersionDetailModel extends MarkdownDocumentVersionModel {
    content: string;
    // Line diff between this version's snapshot and the current document content.
    structuredDiff: DiffModel;
}

export interface ContentItem {
    path: string;
    fullPath: string;
    name: string;
    size: number;
    contentType: string;
    url?: string;
    lastModified: string;
}

export interface MoveFileRequest {
    sourcePath: string;
    destinationPath: string;
}

declare global {
    interface Window {
        onAuthenticated: (authResponse: AuthTokens) => void;
        onSocialAuthLogin: (socialAuthResult: SocialAuthResult) => void;
    }
}

// Status and type enums
// Base interfaces
export interface PersonInfo {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string | null;
}

// AI Assistant types
export interface AssistantInfoModel {
    id: string;
    key: string;
    name: string;
    description: string;
    userInfo: string;
}

export interface AssistantModel extends AssistantInfoModel {
    instructions: string;
    tools: string[];
}

export interface CreateAssistantRequest {
    key: string;
    name: string;
    description: string;
    userInfo: string;
    instructions: string;
    toolNames: string[];
}

export interface UpdateAssistantRequest {
    key: string;
    name: string;
    description: string;
    userInfo: string;
    instructions: string;
    toolNames: string[];
}

export interface AvailableToolModel {
    name: string;
    description: string;
}

export interface IdResult {
    id: string;
}

// ----------------------------------------------------

export enum AllocationType {
    Baseline = 0,
    AdditionalDemand = 1
}

// Which side of a change/activity an assignment sits on. Delivering is the
// default — every assignment before this field existed was implicitly the
// delivery side. Receiving marks the audience the change lands on (teams being
// trained, an SME attending briefings, a receiving seat planned as a role
// placeholder). KEEP IN SYNC with ChangeInvolvement.cs.
export enum ChangeInvolvement {
    Delivering = 0,
    Receiving = 1
}

// How a person is engaged by the organisation — a reporting classification,
// never an allocation rule (a contractor's project work is still their
// Baseline). "Contingent" and "employee" are derived groupings that live in
// lib/workerTypeUtils.ts, never stored values. Unspecified is the schema
// default; normal creation flows set a value explicitly.
// KEEP IN SYNC with WorkerType.cs.
export enum WorkerType {
    Unspecified = 0,
    Permanent = 1,
    FixedTerm = 2,
    Intern = 3,
    Contractor = 4,
    Freelancer = 5,
    Consultant = 6,
    AgencyTemp = 7,
    VendorSow = 8
}

export enum AllocationTargetType {
    Person,
    Role,
    Team
}

export enum TeamPersonType {
    Leader = 0,
    Member = 1
}

export enum AllocationRAGStatus {
    Green = 0,
    Amber = 1,
    Red = 2
}

export enum PhaseType {
    Delivery = 0,
    PostDelivery = 1
}

export enum ProjectStatus {
    Draft = 0,
    Published = 1
}

export enum ProjectVisibility {
    NonDisclosed = 0,
    Disclosed = 1
}

// How likely the work is to proceed. Part of the published picture rule:
// Tentative work is excluded from the default numbers and never triggers
// notifications; opt it in via the What-if lens. Never affects visibility.
// Distinct from ProjectStatus: a Draft can be fully Confirmed work that
// simply isn't ready to publish yet. KEEP IN SYNC with ProjectCertainty.cs.
export enum ProjectCertainty {
    Confirmed = 0,
    Tentative = 1
}

// What has happened to a project's plan — orthogonal to status/visibility/
// certainty. OnHold (funding freeze) and Cancelled projects leave the default
// numbers like drafts do, but every record survives: resume or un-cancel
// restores them, optionally re-timed. KEEP IN SYNC with ProjectLifecycle.cs.
export enum ProjectLifecycle {
    Active = 0,
    OnHold = 1,
    Cancelled = 2
}

export interface PersonalAvailability {
    id: string;
    personId: string;
    name: string; // Added
    description: string | null;
    startDate: Date;
    endDate: Date;
    hoursPerDay: number | null; // If null, defaults to unavailable (0 hours). Otherwise, available hours per day.
    recalculateWeeks: boolean; // Added
}

export interface PersonalAvailabilityWeek {
    id: string;
    personalAvailabilityId?: string;
    personId: string;
    globalWeekNumber: number;
    availableHours: number; // Total hours available for the week (0-168)
    source: 'calculated' | 'manual'; // Calculated from PersonalAvailability or manually edited
    description: string | null;
    lastModified: string; // ISO date string
}

export interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    // True when the email belongs to a linked user account with a verified
    // email. Such an email is that user's confirmed identity and is read-only.
    emailLocked?: boolean;
    // Linked to a user account, so they can sign in.
    hasLogin?: boolean;
    // When an invitation to sign in was last emailed (ISO), if ever.
    invitedAt?: string | null;
    phoneNumber: string | null;
    jobTitle: string | null;
    // How they're engaged (see the WorkerType enum). Optional because
    // locally-constructed objects may omit it; read via ?? WorkerType.Unspecified.
    workerType?: WorkerType;
    teamIds: string[]; // Can belong to multiple teams
    roleIds: string[]; // e.g., ['architect', 'developer']
    // Tags applied to this person — lets server-paged rows render tags without
    // the state's personTags map (which only covers loaded people when /state
    // is scoped). Optional: locally-constructed objects may omit it.
    tagIds?: string[];
    customerRoles: CustomerRole[]; // Organization-wide business roles
    // Utilisation lens (opt-in). When true this person appears in utilisation
    // views/rollups; target is the % of available time we want used (null/undefined
    // falls back to the org default). See docs/product/utilisation.md.
    tracksUtilisation: boolean;
    utilisationTargetPercent?: number | null;
    createdAt?: string; // ISO date string — when the person was first created
    expiredOn?: string | null; // ISO date string when the person left; null = active
    // Candidate-match breakdown; present only on rows from a people search that
    // was given requirement tags (the role → person staffing picker).
    match?: {
        roleMatch: boolean;
        // Required tag ids this person satisfies, across all levels (holding a
        // descendant satisfies a required parent tag).
        matchedRequiredTagIds: string[];
        totalRequired: number;
        // Must-level criteria the person does NOT satisfy. Non-empty =
        // unsuitable for the role as specified — flagged, never hidden.
        missingMustTagIds: string[];
        // Window availability — present only when the search carried a
        // fromWeek/toWeek staffing window. Server-computed over the ACTUALS
        // picture through the real calc engine; display-only.
        windowHeadroomHours?: number | null;
        windowWorstRag?: string | null; // "Green" | "Amber" | "Red"
        reducedAvailabilityWeeks?: number | null;
        topCommitments?:
            | {projectId: string; projectName: string; hours: number}[]
            | null;
    };
}

export interface TeamMember {
    personId: string;
    type: TeamPersonType;
}

export interface Team {
    id: string;
    name: string;
    description?: string;
    members: TeamMember[];
    createdAt?: string; // ISO date string — when the team was first created
    expiredOn?: string | null; // ISO date string when the team was disbanded; null = active
}

export interface Role {
    id: string;
    name: string; // e.g., "Senior Architect", "Developer"
    description: string | null;
    createdAt?: string; // ISO date string — when the role was first created
    expiredOn?: string | null; // ISO date string when the role was retired; null = active
}

export interface Project {
    id: string;
    name: string;
    description: string | null;
    // Optional short reference ("PRJ-042"), unique per customer. Null/absent = none.
    code?: string | null;
    startGlobalWeekNumber: number;
    startOn: string; // ISO DateTime when the project starts
    endGlobalWeekNumber: number;
    endOn: string; // ISO DateTime End of the end week
    durationInWeeks: number; // Duration in weeks
    status: ProjectStatus;
    visibility: ProjectVisibility;
    // Tentative work sits outside the default numbers (opt in via What-if).
    certainty: ProjectCertainty;
    // 0-100, only meaningful when Tentative (cleared on Confirm).
    confidencePercent?: number | null;
    // OnHold/Cancelled sit outside the default numbers too. The server always
    // sends it; absent (pre-lifecycle fixtures) counts as Active — readers use
    // `?? Active`.
    lifecycle?: ProjectLifecycle;
    lifecycleChangedOn?: string | null; // ISO DateTime of last change
    lifecycleNote?: string | null;
    // Effective-dated hold/cancellation: demand before this global week stays
    // in the default numbers; from it onward it drops. Null/undefined = the
    // whole project is excluded (the original binary behaviour).
    lifecycleFromWeek?: number | null;
}

export interface ProjectPhase {
    id: string;
    projectId: string;
    name: string;
    description: string;
    startGlobalWeekNumber: number;
    startOn: string; // ISO DateTime - Monday of start week
    endGlobalWeekNumber: number;
    endOn: string; // ISO DateTime - Sunday of end week
    durationInWeeks: number;
    type: PhaseType;
    colour: string; // Hex color code (format: #RRGGBB)
    createdAt: string; // ISO DateTime
    updatedAt: string; // ISO DateTime
}

export interface ResourceProjectAssignment {
    id: string; // Stable GUID - doesn't change when resource changes
    projectId: string;
    targetType: AllocationTargetType;
    personId: string | null;
    teamId: string | null;
    jobRoleId: string | null;
    allocationType: AllocationType; // Baseline or Additional demand
    // Delivering or Receiving. Optional (?? Delivering at read sites) so
    // pre-WS-E cached state stays valid — matching the AssignmentTag.level
    // convention.
    involvement?: ChangeInvolvement;
    label?: string; // Optional label to distinguish assignments (e.g., "Frontend Dev")
    createdAt: Date;
    notes: string | null;
}

export interface Allocation {
    id: string;
    assignmentId: string; // References ResourceProjectAssignment
    projectId: string; // Denormalized for query performance
    personId: string | null;
    teamId: string | null;
    jobRoleId: string | null;
    globalWeekNumber: number;
    hours: number;
    type: AllocationType; // Denormalized
    involvement?: ChangeInvolvement; // Denormalized from the assignment
    notes: string | null;
}

export interface NonWorkingDay {
    id: string;
    name: string; // e.g., "Christmas Day", "Company Shutdown"
    startDate: Date;
    endDate: Date;
    hoursPerDay: number | null; // If null, defaults to full day off (0 hours). Otherwise, reduced hours per day.
    description: string | null;
}

// Capacity-affecting config is effective-dated: the state carries a version
// series per config (in force from effectiveFromWeek until the next version)
// resolved as-of each week by the calc engine, plus a current-week resolution
// for display/edit surfaces. effectiveFromWeek 1 = since inception.
export interface WorkingHoursConfig {
    effectiveFromWeek: number;
    baseHoursPerWeek: number; // Default: 40
    defaultHoursPerDay: number; // Default: 8 (should align with baseHoursPerWeek / 5)
}

export interface RagThresholdConfig {
    effectiveFromWeek: number;
    // Defaults 50/70 (% of available hours) — deliberately low because
    // additional demand sits on top of a person's day job.
    additionalDemandAmberThreshold: number; // Default: 50
    additionalDemandRedThreshold: number; // Default: 70
    // Opt-in amber band for BASELINE load as a % of available hours (e.g. 92.5
    // = amber from 37 of 40 contracted hours). Null = off: baseline stays hard
    // green->red (the default).
    baselineAmberThreshold: number | null;
}

export interface UtilisationConfig {
    effectiveFromWeek: number;
    // Default target % for tracked people without their own target.
    defaultTargetPercent: number; // Default: 80
    // When false (default) only Baseline counts as "utilised"; when true
    // Additional Demand is also counted. See docs/product/utilisation.md.
    includeAdditionalDemand: boolean;
}

// Which entity types a tag may be applied to (mirrors the backend [Flags] TagScope).
// People also covers role allocations (a role allocation is matched to a person),
// so there is no separate Assignment scope. Bit 4 is retired (old Assignment) and
// Team takes bit 8. Bitwise: Project=1, Person=2, Team=8, Any=11.
export enum TagScope {
    None = 0,
    Project = 1,
    Person = 2,
    Team = 8,
    Any = 11
}

export interface Tag {
    id: string | null; // null for new/unsaved tags
    name: string;
    colour: string; // Hex color code (e.g., "#3B82F6")
    icon?: string; // Icon name from lucide-react (optional)
    isActive: boolean; // Whether the tag is active/visible
    createdAt: string; // ISO date string
    expiredOn?: string | null; // ISO date string when the tag was retired; null = active
    parentTagId?: string | null; // null => top-level dimension; otherwise parent in hierarchy
    appliesTo?: TagScope; // which entity types this tag may be applied to
}

// Helper type for tags with temporary IDs (used during creation)
export interface TagWithTempId extends Omit<Tag, 'id'> {
    id: string | null;
    tempId?: string; // Temporary ID for UI tracking
}

export interface ProjectTag {
    id: string;
    projectId: string;
    tagId: string;
}

export interface PersonTag {
    id: string;
    personId: string;
    tagId: string;
}

// How much a requirement tag on a role allocation matters when matching
// candidates (mirrors the backend RequirementLevel). Ordered strongest-first so
// "strongest wins" deduplication can take the minimum. Missing a Must flags a
// candidate unsuitable but never hides them.
export enum RequirementLevel {
    Must = 0,
    Should = 1,
    Could = 2
}

/** A requirement tag with how much it matters — the shape candidates are matched against. */
export interface RequirementRef {
    tagId: string;
    level: RequirementLevel;
}

export interface AssignmentTag {
    id: string;
    assignmentId: string;
    tagId: string;
    // How much the requirement matters for matching. The server always sends
    // it; absent (pre-level fixtures) counts as Must — readers use `?? Must`.
    level?: RequirementLevel;
}

export interface TeamTag {
    id: string;
    teamId: string;
    tagId: string;
}

export interface PersonContext {
    personId: string;
    canCreateProjects: boolean;
    // Maintainer-seat model flags (optional for older payloads).
    isMaintainer?: boolean;
    isAdministrator?: boolean;
    projectPermissions: Record<string, ProjectPermission>;
    lastUpdatedUtc: string;
}

export interface ProjectPermission {
    projectId: string;
    canEdit: boolean;
    canEditResources: boolean;
}

export interface ProjectPerson {
    id: string;
    projectId: string;
    personId: string;
    projectRole: string; // Custom role (max 100 chars)
    canEdit: boolean; // Can edit project details
    canEditResources: boolean; // Can edit resource allocations
    createdAt: string; // ISO DateTime
}

// State for the entire application
export interface ResourceAllocationState {
    people: Record<string, Person>;
    teams: Record<string, Team>;
    roles: Record<string, Role>;
    projects: Record<string, Project>;
    phases: Record<string, ProjectPhase>;
    assignments: Record<string, ResourceProjectAssignment>; // NEW: The assignment entity
    allocations: Record<string, Allocation>;
    nonWorkingDays: Record<string, NonWorkingDay>;
    personalAvailability: Record<string, PersonalAvailability>;
    personalAvailabilityWeeks: Record<string, PersonalAvailabilityWeek>;
    // Current-week resolutions (display/edit surfaces) …
    workingHoursConfig: WorkingHoursConfig;
    ragThresholdConfig: RagThresholdConfig;
    utilisationConfig: UtilisationConfig;
    // … and the effective-dated series (ascending effectiveFromWeek) the calc
    // engine resolves as-of each week. Optional: absent in older payloads.
    workingHoursConfigVersions?: WorkingHoursConfig[];
    ragThresholdConfigVersions?: RagThresholdConfig[];
    utilisationConfigVersions?: UtilisationConfig[];
    tags: Record<string, Tag>;
    projectTags: Record<string, ProjectTag>;
    personTags: Record<string, PersonTag>;
    assignmentTags: Record<string, AssignmentTag>;
    teamTags: Record<string, TeamTag>;
    projectPeople: Record<string, ProjectPerson>;
    // Assignment ids that have any booked hours across ALL weeks (server-computed),
    // so the grid's "awaiting allocation" flag stays correct when `allocations` is
    // windowed. See docs/engineering/architecture/record-lifecycle-and-state-loading.md (Phase 2).
    allocatedAssignmentIds: string[];
    currentPersonId: string | null;
    // Phase 3 scoped loading: which rule produced this state ("Full" |
    // "Referenced") plus tenant totals, so a scoped client can tell the maps
    // are partial and still show "N people total". Optional: absent in older
    // payloads and the initial empty state.
    scope?: 'Full' | 'Referenced';
    totalActivePeople?: number;
    totalActiveTeams?: number;
}

// View mode
export type ViewMode = 'project' | 'resource' | 'utilisation' | 'delivery';

// Helper type for grid row data
export interface GridRow {
    id: string;
    name: string;
    type: 'project' | 'person' | 'team' | 'role';
}

// Helper type for calculating allocations per cell
export interface CellAllocation {
    totalHours: number;
    allocations: Allocation[];
    isOverallocated: boolean;
}

// PAT types
export interface PersonalAccessTokenDto {
    id: string;
    name: string;
    tokenPrefix: string;
    createdAt: string;
    expiresAt: string | null;
    lastUsedAt: string | null;
}

export interface CreatePersonalAccessTokenRequest {
    name: string;
    expirationDays: number | null;
}

export interface CreatePersonalAccessTokenResponse {
    token: string;
    pat: PersonalAccessTokenDto;
}

export interface PaddleWebhookEventModel {
    id: string;
    paddleEventId: string;
    eventType: string;
    occurredAt: string;
    receivedAt: string;
    processedSuccessfully: boolean;
    exceptionDetails: string | null;
    rawPayload: string;
}

// ── Billing settings (admin console) ───────────────────────────────

/** The billing mode: 'none' is billing off. Profiles exist for the other two. */
export type BillingEnvironment = 'none' | 'sandbox' | 'production';

export type BillingProfileEnvironment = Exclude<BillingEnvironment, 'none'>;

export type BillingInterval = 'monthly' | 'annual';

export type BillingPriceSetStatus = 'Draft' | 'OnSale' | 'Retired';

export interface BillingProfileIssueModel {
    severity: 'error' | 'warning';
    message: string;
}

export interface BillingPriceSetModel {
    id: string;
    environment: BillingProfileEnvironment;
    name: string;
    status: BillingPriceSetStatus;
    notes: string | null;
    platformFeePriceId: string;
    adminSeatsPriceId: string;
    resourceBlocksPriceId: string;
    platformFeeAnnualPriceId: string;
    adminSeatsAnnualPriceId: string;
    resourceBlocksAnnualPriceId: string;
    includedMaintainerSeats: number;
    includedResources: number;
    seatsPerBlock: number;
    resourcesPerBlock: number;
    createdAt: string;
    retiredAt: string | null;
    subscribers: number;
    issues: BillingProfileIssueModel[];
    readyForSale: boolean;
}

export interface BillingProfileModel {
    environment: BillingProfileEnvironment;
    isSaved: boolean;
    isActive: boolean;
    hasApiKey: boolean;
    apiKeyHint: string | null;
    hasWebhookSecret: boolean;
    webhookSecretHint: string | null;
    clientSideToken: string;
    platformFeeProductId: string;
    adminSeatsProductId: string;
    resourceBlocksProductId: string;
    subscriptionSignupEnabled: boolean;
    updatedAt: string | null;
    activatedAt: string | null;
    activatedBy: string | null;
    priceSets: BillingPriceSetModel[];
    issues: BillingProfileIssueModel[];
    readyToActivate: boolean;
}

export interface BillingDataSummaryModel {
    customers: number;
    customersByStatus: Record<string, number>;
    activeOverrides: number;
    paddleSubscriptions: number;
    linkedCustomers: number;
    localTrials: number;
    webhookEvents: number;
}

export interface BillingSettingsModel {
    activeEnvironment: BillingEnvironment;
    source: 'profile' | 'configuration';
    enforcementActive: boolean;
    pastDueGraceDays: number;
    webhookUrl: string;
    profiles: BillingProfileModel[];
    data: BillingDataSummaryModel;
}

export interface SaveBillingProfileRequest {
    apiKey: string | null;
    webhookSecret: string | null;
    clientSideToken: string;
    platformFeeProductId: string;
    adminSeatsProductId: string;
    resourceBlocksProductId: string;
    subscriptionSignupEnabled: boolean;
}

export interface SaveBillingPriceSetRequest {
    name: string;
    notes: string | null;
    platformFeePriceId: string;
    adminSeatsPriceId: string;
    resourceBlocksPriceId: string;
    platformFeeAnnualPriceId: string;
    adminSeatsAnnualPriceId: string;
    resourceBlocksAnnualPriceId: string;
    includedMaintainerSeats: number;
    includedResources: number;
    seatsPerBlock: number;
    resourcesPerBlock: number;
}

export type BillingCatalogItemKey =
    | 'platformFee'
    | 'adminSeats'
    | 'resourceBlocks';

export interface BillingCatalogItemModel {
    key: BillingCatalogItemKey;
    interval: BillingInterval;
    priceId: string;
    productId: string;
    found: boolean;
    error: string | null;
    productMatches: boolean | null;
    intervalMatches: boolean | null;
    savingVersusMonthlyPercent: number | null;
    priceName: string | null;
    description: string | null;
    productName: string | null;
    amount: number | null;
    currencyCode: string | null;
    billingInterval: string | null;
    billingFrequency: number | null;
    trialInterval: string | null;
    trialFrequency: number | null;
    status: string | null;
    productStatus: string | null;
}

export interface BillingCatalogModel {
    priceSetId: string;
    environment: BillingProfileEnvironment;
    error: string | null;
    warnings: string[];
    items: BillingCatalogItemModel[];
}

export interface SwitchBillingEnvironmentRequest {
    environment: BillingEnvironment;
    clearBillingData: boolean;
    clearWebhookLog: boolean;
    restartTrialsUntil: string | null;
    /** Customers to put outside billing as part of this switch. */
    exemptCustomerIds: string[];
    /** Going live with restarted trials: email those customers that billing is starting. */
    notifyCustomers: boolean;
    confirmation?: string;
}

export interface BillingSwitchCustomerModel {
    id: string;
    name: string;
    currentReason: string;
    hasPaddleSubscription: boolean;
    exempt: boolean;
    modeAfter: SubscriptionEnforcementMode;
    reasonAfter: string;
}

export interface BillingRestrictedCustomerModel {
    id: string;
    name: string;
    mode: SubscriptionEnforcementMode;
    reason: string;
}

export interface BillingSwitchOutcomeModel {
    environment: BillingEnvironment;
    enforcementActiveAfter: boolean;
    blockers: string[];
    paddleSubscriptionsRemoved: number;
    customersUnlinked: number;
    webhookEventsRemoved: number;
    trialsRestarted: number;
    customersExempted: number;
    customersNotified: number;
    customers: BillingSwitchCustomerModel[];
    customersFull: number;
    customersReadOnly: number;
    customersBillingOnly: number;
    restrictedCustomers: BillingRestrictedCustomerModel[];
}

// ── Subscription Status ────────────────────────────────────────────

export interface SubscriptionStatusModel {
    status: string;
    isActive: boolean;
    isTrialing: boolean;
    trialEndsAt: string | null;
    trialDaysRemaining: number | null;
    nextBillDate: string | null;
    canceledAt: string | null;
    paddleCustomerId: string | null;
    updatePaymentMethodUrl: string | null;
    cancelSubscriptionUrl: string | null;
    // Enforcement (Theme 11)
    billingMode: BillingEnvironment;
    /** Outside billing altogether: nothing to pay, nothing to set up. */
    billingExempt: boolean;
    /** Set when billing went live for a customer who had been using Certavue free. */
    billingTransitionAnnouncedAt: string | null;
    enforcementActive: boolean;
    enforcementMode: SubscriptionEnforcementMode;
    enforcementReason: string;
    gracedUntil: string | null;
    maxMaintainerSeats: number;
    usedMaintainerSeats: number;
    maxResources: number;
    usedResources: number;
    overrideActive: boolean;
    overrideExpiresOn: string | null;
    items: SubscriptionItemModel[];
}

export type SubscriptionEnforcementMode = 'Full' | 'ReadOnly' | 'BillingOnly';

export interface SubscriptionItemModel {
    subscriptionId: string;
    paddleSubscriptionId: string | null;
    paddleProductId: string | null;
    paddlePriceId: string | null;
    paddleStatus: string | null;
    quantity: number;
    isActive: boolean;
    startDate: string;
    endDate: string | null;
    nextBillDate: string | null;
    trialEndsAt: string | null;
}
